<template>
  <Teleport to="body">
    <Transition name="players-overlay">
      <div v-if="visible" class="PlayersOverlay" @click.self="emit('close')">
        <div class="players-wrapper" :class="{ 'is-mobile': isMobile }">
          <div ref="cardRef" class="players-card">
            <div class="players-tabs">
              <div :class="['players-tab', { active: activeTab === 'following' }]" @click="activeTab = 'following'">
                {{ localize('players.general.tabs.following') }}
              </div>
              <div :class="['players-tab', { active: activeTab === 'followers' }]" @click="activeTab = 'followers'">
                {{ localize('players.general.tabs.followers') }}
              </div>
            </div>

            <div class="players-scroll">
              <div class="players-content">
                <template v-if="isLoading">
                  <div class="loading-state">
                    <div v-for="n in 4" :key="n" class="skeleton-row">
                      <div class="skeleton-avatar skeleton" />
                      <div class="skeleton-text skeleton" />
                    </div>
                  </div>
                </template>

                <template v-else-if="currentList.length">
                  <div
                    v-for="player in currentList"
                    :key="player.id"
                    class="player-row"
                    @click="handlePlayerClick(player.username)"
                  >
                    <div class="player-avatar">
                      <img
                        v-if="player.profileImageBlobHash"
                        :src="getImageSrc(player.profileImageBlobHash)"
                        class="picture"
                      />
                      <GamIcon v-else :name="GamIconName.PAWN_EMPTY" />
                    </div>
                    <span class="player-username">@{{ player.username }}</span>
                  </div>
                </template>

                <template v-else>
                  <div class="empty-state">
                    <GamIcon :name="GamIconName.PAWN_EMPTY" size="24px" is-disabled />
                    <span>{{ emptyMessage }}</span>
                  </div>
                </template>
              </div>
            </div>

            <div class="players-close" @click="emit('close')" @touchend.prevent="emit('close')">
              <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
import type { BaseUserDto } from '@/core/data/user/user.interface';
import { gambits, localize } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { useDrawerGesture } from '@/views/composables/hooks/useDrawerGesture';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

type TabType = 'followers' | 'following';

const props = defineProps<{
  visible: boolean;
  initialTab?: TabType;
}>();

const emit = defineEmits<{
  close: [];
}>();

const router = useRouter();

const activeTab = ref<TabType>(props.initialTab ?? 'following');
const isLoading = ref(false);
const cardRef = ref<HTMLElement | null>(null);
const followers = ref<BaseUserDto[]>([]);
const following = ref<BaseUserDto[]>([]);

useDrawerGesture(cardRef, () => emit('close'));

const currentList = computed(() => (activeTab.value === 'followers' ? followers.value : following.value));

const emptyMessage = computed(() =>
  activeTab.value === 'followers'
    ? localize('players.modal.empty.followers')
    : localize('players.modal.empty.following'),
);

function getImageSrc(hash: string): string {
  return gambits.imageService.getImageSrc(hash, 100);
}

function handlePlayerClick(username: string) {
  emit('close');
  router.push({ path: `/@${username}` });
}

async function fetchPlayers() {
  isLoading.value = true;

  const [followersResult, followingResult] = await Promise.all([
    gambits.userService.getFollowers(),
    gambits.userService.getFollowing(),
  ]);

  followers.value = followersResult.data ?? [];
  following.value = followingResult.data ?? [];
  isLoading.value = false;
}

/* Immediate because returning from a player's profile rebuilds PlayerDetail,
   which reopens us from the route query — so we mount already visible and there
   is no false-to-true transition to catch. */
watch(
  () => props.visible,
  (v) => {
    if (v) {
      activeTab.value = props.initialTab ?? 'following';
      fetchPlayers();
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.PlayersOverlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.players-wrapper {
  position: relative;
  width: 380px;
  max-width: calc(100vw - 2rem);
  height: min(700px, calc(100vh - 4rem));
  display: flex;
  flex-direction: column;

  &.is-mobile {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    height: 85vh;

    .players-card {
      border-radius: 1.5rem 1.5rem 0 0;
    }
  }
}

.players-card {
  position: relative;
  flex: 1;
  min-height: 0;
  border-radius: 1.5rem;
  border: 1px solid var(--color-white-10);
  background: var(--color-dark-700);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  will-change: transform;
}

.players-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.players-tabs {
  display: flex;
  align-items: stretch;
  width: 100%;
  flex-shrink: 0;
  background: var(--color-dark-700);
  border-bottom: 1px solid var(--color-white-10);
  z-index: 1;
}

.players-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-s);
  padding: var(--spacing-l) 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-white-30);
  cursor: pointer;
  border-bottom: none;
  transition: color var(--transition);
  position: relative;

  &.active {
    color: var(--color-white-70);
  }

  /* The selected half of the line under the tabs. */
  &.active::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 1px;
    background: var(--color-white);
  }

  @include hover-capable {
    &:hover:not(.active) {
      color: var(--color-white-50);
    }
  }
}

.players-content {
  padding: var(--spacing-l);
  padding-bottom: var(--safe-bottom-offset);
}

.player-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  cursor: pointer;
  transition: opacity var(--transition);

  @include hover {
    opacity: 0.7;
  }
}

.player-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-dark-500);
  flex-shrink: 0;
  color: var(--color-white-30);

  .picture {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.player-username {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-white-100);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xxl) 0;
  color: var(--color-white-20);
  font-size: 14px;
}

.empty-hints {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  padding-top: var(--spacing-xl);
  color: var(--color-white-20);
  font-size: 13px;

  .empty-hint {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.skeleton-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

.skeleton-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.skeleton-text {
  height: 14px;
  width: 120px;
  border-radius: 4px;
}

.skeleton {
  background: var(--color-white-5);
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.8;
  }
}

.players-close {
  position: absolute;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 3;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press($transform: translateX(-50%));
}

.players-overlay-enter-active,
.players-overlay-leave-active {
  transition: opacity 200ms ease;

  .players-wrapper {
    transition: transform 200ms ease;
  }
}

.players-overlay-enter-from,
.players-overlay-leave-to {
  opacity: 0;

  .players-wrapper {
    transform: translateY(20px);
  }
}

.players-overlay-enter-from .is-mobile,
.players-overlay-leave-to .is-mobile {
  transform: translateY(100%);
}
</style>
